import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext.js";
import { callXeroFunction } from "../firebase.js";

export default function FeedbackManagerPage() {
  const { language } = useLanguage();
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({ pending: 0, positive: 0, negative: 0, cancelled: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);

  // Modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [notes, setNotes] = useState("");

  // Clear test data modal
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearPhone, setClearPhone] = useState("");
  const [clearLoading, setClearLoading] = useState(false);
  const [clearResult, setClearResult] = useState(null);

  const t = {
    pt: {
      title: "Gerenciador de Feedback",
      subtitle: "Gerencie solicitações de feedback WhatsApp",
      all: "Todos",
      pending: "Pendentes",
      positive: "Positivos",
      negative: "Negativos",
      cancelled: "Cancelados",
      client: "Cliente",
      phone: "Telefone",
      sentAt: "Enviado em",
      status: "Status",
      response: "Resposta",
      actions: "Ações",
      cancel: "Cancelar",
      markReceived: "Marcar Recebido",
      reactivate: "Reativar",
      noFeedbacks: "Nenhum feedback encontrado",
      noFeedbacksDesc: "Quando você enviar pedidos de feedback, eles aparecerão aqui.",
      awaitingResponse: "Aguardando resposta",
      positiveResponse: "Positivo",
      negativeResponse: "Negativo",
      externalResponse: "Recebido externamente",
      cancelledStatus: "Cancelado",
      statsTitle: "Estatísticas",
      confirmCancel: "Cancelar este feedback?",
      confirmCancelDesc: "O cliente não receberá mais follow-ups automáticos.",
      notesPlaceholder: "Adicione uma nota (opcional)...",
      notesTitle: "Marcar como Recebido",
      notesDesc: "Cliente deu feedback por outro canal? Adicione uma nota e marque como recebido.",
      save: "Salvar",
      close: "Fechar",
      followUp7Days: "Follow-up 7 dias enviado",
      followUp20Days: "Follow-up 20 dias enviado",
      reviewLinkClicked: "Link Google Review clicado",
      nextFollowUp: "Próximo follow-up",
      days: "dias",
      clearTestData: "Limpar Dados de Teste",
      clearTestDataTitle: "Limpar Dados de Teste",
      clearTestDataDesc: "Digite o número de telefone para limpar todos os dados de feedback e review associados. Use para testes.",
      phonePlaceholder: "Ex: 64275954995",
      clear: "Limpar",
      clearSuccess: "Dados limpos com sucesso!",
      feedbacksDeleted: "feedbacks deletados",
      reviewsDeleted: "reviews deletados",
      pendingDeleted: "pendentes deletados",
    },
    en: {
      title: "Feedback Manager",
      subtitle: "Manage WhatsApp feedback requests",
      all: "All",
      pending: "Pending",
      positive: "Positive",
      negative: "Negative",
      cancelled: "Cancelled",
      client: "Client",
      phone: "Phone",
      sentAt: "Sent at",
      status: "Status",
      response: "Response",
      actions: "Actions",
      cancel: "Cancel",
      markReceived: "Mark Received",
      reactivate: "Reactivate",
      noFeedbacks: "No feedbacks found",
      noFeedbacksDesc: "When you send feedback requests, they will appear here.",
      awaitingResponse: "Awaiting response",
      positiveResponse: "Positive",
      negativeResponse: "Negative",
      externalResponse: "Received externally",
      cancelledStatus: "Cancelled",
      statsTitle: "Statistics",
      confirmCancel: "Cancel this feedback?",
      confirmCancelDesc: "The client will no longer receive automatic follow-ups.",
      notesPlaceholder: "Add a note (optional)...",
      notesTitle: "Mark as Received",
      notesDesc: "Client gave feedback through another channel? Add a note and mark as received.",
      save: "Save",
      close: "Close",
      followUp7Days: "7-day follow-up sent",
      followUp20Days: "20-day follow-up sent",
      reviewLinkClicked: "Google Review link clicked",
      nextFollowUp: "Next follow-up",
      days: "days",
      clearTestData: "Clear Test Data",
      clearTestDataTitle: "Clear Test Data",
      clearTestDataDesc: "Enter the phone number to clear all feedback and review data associated with it. Use for testing.",
      phonePlaceholder: "E.g.: 64275954995",
      clear: "Clear",
      clearSuccess: "Data cleared successfully!",
      feedbacksDeleted: "feedbacks deleted",
      reviewsDeleted: "reviews deleted",
      pendingDeleted: "pending deleted",
    },
  };

  const text = t[language] || t.en;

  // Fetch feedbacks
  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const statusParam = filter === "all" ? undefined : filter;
      const result = await callXeroFunction("getFeedbacks", { status: statusParam }, { method: "GET" });

      if (result.success) {
        setFeedbacks(result.data || []);
        setStats(result.stats || { pending: 0, positive: 0, negative: 0, cancelled: 0, total: 0 });
      } else {
        setError(result.error || "Failed to fetch feedbacks");
      }
    } catch (err) {
      console.error("Error fetching feedbacks:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Cancel feedback
  const handleCancel = async (id) => {
    if (!window.confirm(text.confirmCancel + "\n" + text.confirmCancelDesc)) return;

    try {
      setActionLoading(id);
      await callXeroFunction("cancelFeedback", {}, { method: "PUT", id, action: "cancel" });
      fetchFeedbacks();
    } catch (err) {
      console.error("Error cancelling feedback:", err);
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Mark as received externally
  const handleMarkReceived = async () => {
    if (!selectedFeedback) return;

    try {
      setActionLoading(selectedFeedback.id);
      await callXeroFunction("markFeedbackReceived", { notes }, { method: "PUT", id: selectedFeedback.id, action: "mark-received" });
      setShowNotesModal(false);
      setNotes("");
      setSelectedFeedback(null);
      fetchFeedbacks();
    } catch (err) {
      console.error("Error marking feedback as received:", err);
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Reactivate cancelled feedback
  const handleReactivate = async (id) => {
    try {
      setActionLoading(id);
      await callXeroFunction("reactivateFeedback", {}, { method: "PUT", id, action: "reactivate" });
      fetchFeedbacks();
    } catch (err) {
      console.error("Error reactivating feedback:", err);
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Clear test data for a phone number
  const handleClearTestData = async () => {
    if (!clearPhone.trim()) return;

    try {
      setClearLoading(true);
      setClearResult(null);

      const result = await callXeroFunction("clearFeedbackTestData", {}, {
        method: "DELETE",
        id: clearPhone.trim()
      });

      if (result.success) {
        setClearResult({
          success: true,
          deleted: result.deleted
        });
        fetchFeedbacks(); // Refresh the list
      } else {
        setClearResult({ success: false, error: result.error });
      }
    } catch (err) {
      console.error("Error clearing test data:", err);
      setClearResult({ success: false, error: err.message });
    } finally {
      setClearLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (feedback) => {
    if (feedback.status === "cancelled") {
      return <Badge bg="secondary">{text.cancelledStatus}</Badge>;
    }
    if (!feedback.response_received) {
      return <Badge bg="warning" text="dark">{text.awaitingResponse}</Badge>;
    }
    if (feedback.response_type === "positive") {
      return <Badge bg="success">{text.positiveResponse}</Badge>;
    }
    if (feedback.response_type === "negative") {
      return <Badge bg="danger">{text.negativeResponse}</Badge>;
    }
    if (feedback.response_type === "external") {
      return <Badge bg="info">{text.externalResponse}</Badge>;
    }
    return <Badge bg="secondary">-</Badge>;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === "pt" ? "pt-BR" : "en-NZ", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate days until next follow-up
  const getDaysUntilFollowUp = (feedback) => {
    if (feedback.response_received || feedback.status === "cancelled") return null;
    if (feedback.follow_up_sent_7days) return null; // Already sent all follow-ups

    const sentAt = new Date(feedback.sent_at);
    const now = new Date();
    const daysSinceSent = Math.floor((now - sentAt) / (1000 * 60 * 60 * 24));
    const daysUntil = 7 - daysSinceSent;

    if (daysUntil <= 0) return "Soon";
    return `${daysUntil} ${text.days}`;
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between flex-wrap gap-2 align-items-center mb-4">
        <div>
          <h2 className="mb-1">{text.title}</h2>
          <p className="text-muted mb-0">{text.subtitle}</p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-warning"
            onClick={() => {
              setShowClearModal(true);
              setClearResult(null);
              setClearPhone("");
            }}
            title={text.clearTestData}
          >
            🧹 {text.clearTestData}
          </Button>
          <Button variant="outline-primary" onClick={fetchFeedbacks} disabled={loading}>
            {loading ? <Spinner size="sm" /> : "🔄"}
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <Row className="mb-4">
        <Col xs={6} md={3} lg={2} className="mb-2">
          <Card className={`text-center ${filter === "pending" ? "border-warning" : ""}`}
                style={{ cursor: "pointer" }} onClick={() => setFilter("pending")}>
            <Card.Body className="py-3">
              <h3 className="mb-0 text-warning">{stats.pending || 0}</h3>
              <small className="text-muted">{text.pending}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={2} className="mb-2">
          <Card className={`text-center ${filter === "positive" ? "border-success" : ""}`}
                style={{ cursor: "pointer" }} onClick={() => setFilter("positive")}>
            <Card.Body className="py-3">
              <h3 className="mb-0 text-success">{stats.positive || 0}</h3>
              <small className="text-muted">{text.positive}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={2} className="mb-2">
          <Card className={`text-center ${filter === "negative" ? "border-danger" : ""}`}
                style={{ cursor: "pointer" }} onClick={() => setFilter("negative")}>
            <Card.Body className="py-3">
              <h3 className="mb-0 text-danger">{stats.negative || 0}</h3>
              <small className="text-muted">{text.negative}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3} lg={2} className="mb-2">
          <Card className={`text-center ${filter === "cancelled" ? "border-secondary" : ""}`}
                style={{ cursor: "pointer" }} onClick={() => setFilter("cancelled")}>
            <Card.Body className="py-3">
              <h3 className="mb-0 text-secondary">{stats.cancelled || 0}</h3>
              <small className="text-muted">{text.cancelled}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={12} lg={4} className="mb-2">
          <Card className={`text-center ${filter === "all" ? "border-primary" : ""}`}
                style={{ cursor: "pointer" }} onClick={() => setFilter("all")}>
            <Card.Body className="py-3">
              <h3 className="mb-0 text-primary">{stats.total || 0}</h3>
              <small className="text-muted">{text.all}</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">{text.noFeedbacks}</h5>
              <p className="text-muted">{text.noFeedbacksDesc}</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>{text.client}</th>
                  <th className="d-none d-md-table-cell">{text.phone}</th>
                  <th>{text.sentAt}</th>
                  <th>{text.status}</th>
                  <th className="d-none d-md-table-cell">{text.response}</th>
                  <th className="d-none d-md-table-cell">{text.nextFollowUp}</th>
                  <th className="text-end">{text.actions}</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback, index) => (
                  <tr key={`${feedback.id}-${index}`}>
                    <td className="fw-medium">{feedback.client_name}</td>
                    <td className="d-none d-md-table-cell">
                      <a href={`https://wa.me/${feedback.client_phone?.replace(/\D/g, "")}`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-decoration-none">
                        {feedback.client_phone}
                      </a>
                    </td>
                    <td>{formatDate(feedback.sent_at)}</td>
                    <td>{getStatusBadge(feedback)}</td>
                    <td className="d-none d-md-table-cell">
                      {feedback.feedback_text ? (
                        <span title={feedback.feedback_text}>
                          {feedback.feedback_text.substring(0, 50)}
                          {feedback.feedback_text.length > 50 ? "..." : ""}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {getDaysUntilFollowUp(feedback) && (
                        <Badge bg="info" className="me-1">{getDaysUntilFollowUp(feedback)}</Badge>
                      )}
                      {feedback.follow_up_sent_7days && (
                        <Badge bg="secondary" className="me-1" title={text.followUp7Days}>7d</Badge>
                      )}
                      {feedback.link_clicked && (
                        <Badge bg="success" title={text.reviewLinkClicked}>✓</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      {actionLoading === feedback.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          {feedback.status === "cancelled" ? (
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => handleReactivate(feedback.id)}
                              title={text.reactivate}
                            >
                              ↩️
                            </Button>
                          ) : !feedback.response_received ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline-success"
                                className="me-1"
                                onClick={() => {
                                  setSelectedFeedback(feedback);
                                  setShowNotesModal(true);
                                }}
                                title={text.markReceived}
                              >
                                ✓
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleCancel(feedback.id)}
                                title={text.cancel}
                              >
                                ✕
                              </Button>
                            </>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Notes Modal */}
      <Modal show={showNotesModal} onHide={() => setShowNotesModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{text.notesTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{text.notesDesc}</p>
          <Form.Group>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder={text.notesPlaceholder}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            {text.close}
          </Button>
          <Button variant="success" onClick={handleMarkReceived} disabled={actionLoading}>
            {actionLoading ? <Spinner size="sm" /> : text.save}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clear Test Data Modal */}
      <Modal show={showClearModal} onHide={() => setShowClearModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>🧹 {text.clearTestDataTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">{text.clearTestDataDesc}</p>
          <Form.Group className="mb-3">
            <Form.Label>Telefone</Form.Label>
            <Form.Control
              type="text"
              placeholder={text.phonePlaceholder}
              value={clearPhone}
              onChange={(e) => setClearPhone(e.target.value)}
              disabled={clearLoading}
            />
          </Form.Group>

          {clearResult && (
            <Alert variant={clearResult.success ? "success" : "danger"}>
              {clearResult.success ? (
                <>
                  <strong>{text.clearSuccess}</strong>
                  <ul className="mb-0 mt-2">
                    <li>{clearResult.deleted?.feedbacks || 0} {text.feedbacksDeleted}</li>
                    <li>{clearResult.deleted?.reviews || 0} {text.reviewsDeleted}</li>
                    <li>{clearResult.deleted?.pending || 0} {text.pendingDeleted}</li>
                  </ul>
                </>
              ) : (
                clearResult.error
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearModal(false)}>
            {text.close}
          </Button>
          <Button
            variant="warning"
            onClick={handleClearTestData}
            disabled={clearLoading || !clearPhone.trim()}
          >
            {clearLoading ? <Spinner size="sm" /> : text.clear}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
